import React, {useState, useEffect, useRef, useContext, forwardRef, useImperativeHandle, useCallback, useMemo} from 'react';
import NotificationCardList from './NotificationsList/NotificationCardList';
import NotificationRecipientsSelectorDialog from '../NotificationThreadConfig/NotificationRecipientsSelectorDialog';
import RichTextEditorToolbar from './Editor/RichTextEditorToolbar';
import RichTextMessageEditor from './Editor/RichTextMessageEditor';
import FileUpload from './Editor/FileUpload';
import NotificationThreadHeader from './NotificationThreadHeader';
import Tooltip from 'react-bootstrap/Tooltip';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import { toast } from 'react-toastify';
import jQuery from "jquery";
import "emojionearea";
import "emojionearea/dist/emojionearea.min.css";

import { CHANNELS } from "../../constant-data";
import { USERS_API_BASE_URL } from '../../constants';
import Recipient from '../../types/PreviewedRecipient';
import { CreateContactGroupRequest } from '../../types/ContactGroupCreation';
import { CreateNotificationRequest } from '../../types/Notification';
import {
    createNewNotification,
    createContactGoup,
    editNotificationService,
    getSelectedRecipientData,
    getUsersDetailBGgroup,

    sendMessage
} from '../../services/notification.services';
import AuthContext from '../../store/auth-context';
import styles from './Editor/ComposeNotification.module.css';
import button from '../Style/ButtonStyle.module.css';
import common from "../Style/common.module.css";

import EditorStyle from "../Style/EditorStyle.module.css";
import '../Style/Table-style.css';
import { getSelectUserGroupForDM } from '../../services/group.service';

// Interface for the RichTextEditor ref methods
interface RichTextEditorRef {
    insertEditorText: (text: string) => void;
    insertImages: (files: any[]) => void;
    insertVideosCustomComponent: (files: any[]) => void;
    insertPDFAttachment: (files: any[]) => void;
    insertImageAttachmentCustomComponent: (files: any[]) => void;
    insertPdfToImageAttachmentCustomComponent: (files: any[]) => void;
    insertAudioAttachmentCustomComponent: (files: any[]) => void;
    reInitializeForEdit: (messageBody: string, rawMessageBody: string) => void;
}

// Interface for component props
interface NotificationThreadProps {
    channels: string[];
    isCardOpen: boolean;
    selectedNotificationThreadUuid: string | null;
    emailOnlySubscribers: any[];
    selectedNtification: any[];
    notificationThreadStatus: string;
    selectedGroupTitle: string;
    currentSelectedGroup: any;
    selectUsersForGroup: any;
    selectedUsers: string[];
    addNotificationsToList: (message: any, isCreatingRecipient: boolean) => void;
    updateNotificationAtUuid: (id: string, message: string, rawMessage: string) => void;
    updateNotificationFlagAtUuid: (id: string, flag: string) => void;
    resetNotificationBox: () => void;
    onCreatePinMessage: (messageId: string) => void;
    setIsEditing: (editing: boolean) => void;
    setSelectUsersForGroup: (data: any) => void;
    setSelectedNotificationThreadUuid: (uuid: string | null) => void;
    onRemoveNotification: (id: string, shouldRemoveGroup: boolean) => void;
    loadNotificationThreads?: (pageNumber: number, searchText: string) => Promise<void>;
    onSelectedNotification: (id: string, notificationThreadStatus: string, channels: string[], title: string) => void;
    onNavigateToDmThread?: (groupUuid: string, notificationThreadStatus: string, channels: string[], title: string) => void;
    setSelectedGroupTitle: (title: string) => void;
}

// Extend Window interface for custom global properties
declare global {
    interface Window {
        contentBlocksType: Record<string, number>;
        emoj: any;
        UPLOAD_FILES: string;
    }
}

window.contentBlocksType = {
    "IMAGES_GALLERY": 1,
    "VIDEOS": 2,
    "PDF_ATTACHMENTS": 3,
    "IMAGE_ATTACHMENTS": 4,
    "AUDIO_ATTACHMENTS": 5,
    "PDF_TO_IMAGE_ATTACHMENTS": 6
}

const SVGIcon = (props) => {
    return <>
        <span className={common.channelIconEdit} dangerouslySetInnerHTML={{ __html: props.svgIcondata }}></span>
    </>
}

// Content type mapping (moved outside component to avoid recreation on every render)
const CONTENT_TYPE_MAP = {
    'ARTICLES': 'fd5c3807-135a-4b49-a6d7-7ad6c49c8761',
    'NEWSLETTERS': '09cb889b-3cb0-4ede-98e7-a9f3a797bcd5',
    'SURVEYS': 'effac112-cd2d-44ac-a163-2e55971d07e2',
    'ESIGNS': '56cb5860-bc8e-49f7-b0c4-d94b160e4eb0'
};

const SUPPORTED_CONTENT_TYPES = ['ARTICLES', 'NEWSLETTERS', 'CALENDARS', 'SURVEYS', 'EFLASHES', 'ESIGNS'];

const NotificationThread = forwardRef<any, NotificationThreadProps>((props, ref) => {
    // Destructure props at the top of the component to use in dependencies
    const {
        channels,
        isCardOpen,
        selectedNotificationThreadUuid,
        emailOnlySubscribers,
        setSelectUsersForGroup: propsSetSelectUsersForGroup,
        setSelectedNotificationThreadUuid: propsSetSelectedNotificationThreadUuid,
        selectedNtification,
        notificationThreadStatus,
        selectedGroupTitle,
        selectUsersForGroup,
        addNotificationsToList,
        updateNotificationAtUuid,
        updateNotificationFlagAtUuid,
        resetNotificationBox,
        onCreatePinMessage,
        onRemoveNotification,
        loadNotificationThreads,
        setSelectedGroupTitle
    } = props;

    // Create a ref to track the current thread UUID to prevent stale updates
    const currentThreadRef = useRef(selectedNotificationThreadUuid);
    
    // Update ref when thread changes
    useEffect(() => {
        currentThreadRef.current = selectedNotificationThreadUuid;
    }, [selectedNotificationThreadUuid]);
    
    // Wrap setSelectUsersForGroup to add thread validation guard
    // This prevents stale API responses from updating the wrong thread's data
    const guardedSetSelectUsersForGroup = useCallback((data: any) => {
        const threadId = selectedNotificationThreadUuid;
        
        // Only update if we have a selected thread (not cleared/switching)
        if (threadId && currentThreadRef.current === threadId) {
            propsSetSelectUsersForGroup(data);
        }
    }, [selectedNotificationThreadUuid, propsSetSelectUsersForGroup]);

    const authCtx = useContext(AuthContext);
    const orgUuid = authCtx.userData.orgId;
    const epubOrg = authCtx.userData.epubOrgId;
    const [notificationSubject, setNotificationSubject] = useState('');
    const [activeEditor, setActiveEditor] = useState('RICH_TEXT');
    const [notificationMessage, setNotificationMessage] = useState('');
    const [rawMessage, setRawMessage] = useState('');
    const [messageToEdit, setMessageToEdit] = useState<any>(false);
    const [createRecipient, setCreateRecipient] = useState<CreateContactGroupRequest>({
        selectedUsers: [],
        selectedTags: [],
        selectedChanels: '',
        name: '',
        createdBy: '',
        emailOnlySubscribers: []
    });
    const getRawMessage = () => {
        return rawMessage
    };
    const [isCreatingRecipient, setIsCreatingRecipient] = useState(false);
    const [hasRecipientsSelected, setHasRecipientsSelected] = useState(false);
    const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [disableEditor, setDisableEditor] = useState(false);
    const [currentContentLengthType, setCurrentContentLengthType] = useState(0);
    const [currentSubjectLenght, setCurrentSubjectLenght] = useState(0);
    const [isMessageInputFocused, setMessageInputFocused] = useState(false);
    const [isSubjectInputFocused, setSubjectInputFocused] = useState(false);
    const [moreTextBlockEditorState, setmoreTextBlockEditorState] = useState();
    const [composeNotification, setComposeNotification] = useState<CreateNotificationRequest>(
        {
            id: '',
            displayOrder: 1,
            priority: 'NORMAL',
            contentType: 'TEXT',
            messageCategory:'',
            messageBody: '',
            rawMessageBody: '',
            lastModified: '',
            createdOn: '',
            status: 'ACTIVE',
            categoryId: '337f5e5d-288b-40d5-be14-901cc3acacc0',
            authorId: authCtx.userData.uuid,
            orgId: orgUuid,
            epubOrgID:epubOrg,
            starsRating: 4,
            readStatus: 0,
            readOn: '',
            authorImageUrl: (authCtx.userData.thumbImage?authCtx.userData.thumbImage :''),
            title: '',
            authorName: authCtx.userData.name,
            messageType: 'MESSAGE',
            isPasswordProtected: 'NO',
            schoolName: "Austnews2",
            isFavorite: 'NO',
            likesCount: 0,
            isLiked: false,
            isPinned: false,
            schoolImage: authCtx.userData.orgLogoUrl,
            usersGroupId: null
        }
    );
    // For contact group Edit
    const [isAllSchoolLinkAppUsersSelected, setIsAllSchoolLinkAppUsersSelected] = useState(false);
    const [isAllEmailOnlySubscribersSelected, setIsAllEmailOnlySubscribersSelected] = useState(false);
    const [privateRecipientDetails, setPrivateRecipientDetails] = useState<Recipient | null>(null);
    const [inPreviewNotificationUuid, setInPreviewNotificationUuid] = useState<string | null>(null);
    const [previewedRecipientReceivedBasedOnConfigurationOfNotificationThread, setPreviewedRecipientReceivedBasedOnConfigurationOfNotificationThread] = useState<any[]>([]);

    const [selectedRecipientsData, setSelectedRecipientsData] = useState<{previewedRecipientData: any[], total: number}>({previewedRecipientData: [], total: 0});
    
    // Helper function to safely parse and format recipient data
    const getFormattedRecipientData = useMemo(() => {
        try {
            let recipients = selectUsersForGroup?.previewedRecipientData;
            
            // Parse if it's a string
            if (typeof recipients === 'string') {
                recipients = recipients.trim() ? JSON.parse(recipients) : [];
            }
            
            // Ensure it's an array
            if (!Array.isArray(recipients)) {
                recipients = [];
            }
            
            return {
                previewedRecipientData: recipients,
                total: selectUsersForGroup?.total || recipients.length,
                memberCountMismatch: selectUsersForGroup?.memberCountMismatch || false
            };
        } catch (error) {
            console.error('Error parsing recipient data:', error);
            return {
                previewedRecipientData: [],
                total: 0,
                memberCountMismatch: false
            };
        }
    }, [selectUsersForGroup]);

    // First message of group notification
    const groupFirstNotificationSubject = selectedNtification.slice(0, 1);
    const richtextEditor = useRef<RichTextEditorRef>(null);
    const refMoreTextBlockEditor = useRef<any>(null);
    const previousFirstUserRef = useRef<string | null>(null);

    const getPrivateRecipientDetail = useCallback(async (selectedUserUuid) => {
        try {
            const requestData = {
                name: "",
                selectedUsers: [selectedUserUuid],
                selectedTags: [],
                selectedCategoriesAndTags: [],
                emailOnlySubscribers: [],
                isAllSchoolLinkAppUsersSelected: false,
                isAllEmailOnlySubscribersSelected: false,
                epubOrgId: "",
                fkOrgID: orgUuid,
                channels:'5',
            };

            const response = await fetch(`${USERS_API_BASE_URL}/contact-groups/preview-recipients`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json, text/plain, */*',
                    'Authorization': authCtx.token,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            const result = await response.json();

            if (result.status === 400 && result.error) {
                // Handle specific error for user already in direct message group
                toast.error(result.error);
                return null;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (result.status === 200 && result.data) {
                setPrivateRecipientDetails(result.data);
                return result.data;
            } else if (result.status === 302) {
                // Status 302: Redirect to existing notification thread
                // This happens when the selected user already has an active thread
                toast.info("A notification thread with this user already exists. Search for the existing thread to continue the conversation.");
                /*setTimeout(() => {
                    const channels = result.data.selectedChannel
                        ? result.data.selectedChannel.split(',')
                        : [];
                    onSelectedNotification(
                        result.data.uuid,
                        result.data.status,
                        channels,
                        result.data.last_notification_title
                    );
                }, 1000);*/

            } else {
                throw new Error(result.error || 'Failed to fetch recipient details');
            }
        } catch (error) {
            console.error('Error fetching private recipient details:', error);
            toast.error('Failed to fetch recipient details');
            return null;
        }
    }, [orgUuid, authCtx.token]);

    useEffect(() => {
        // console.log("User Data", authCtx.userData);
        // console.log("channels", channels);
        window.emoj=false;
        
        // Check if emojioneArea is available
        if (jQuery.fn.emojioneArea) {
            try {
                window.emoj = jQuery("#message-composition-wrapper").emojioneArea({
                    standalone: true,
                    hideSource: true,
                    autocomplete: false,
                    recentEmojis: false
                });
                
                if (window.emoj && window.emoj[0] && window.emoj[0].emojioneArea) {
                    window.emoj[0].emojioneArea.off("emojibtn.click");
                    window.emoj[0].emojioneArea.on("emojibtn.click", function (btn, event) {
                        let emo = window.emoj[0].emojioneArea.getText();
                        //console.log(emo);
                        if (richtextEditor.current) {
                            richtextEditor.current.insertEditorText(emo);
                        }
                        //setNotificationMessage(emo);
                    });
                }
            } catch (error) {
                console.warn("Failed to initialize emojioneArea:", error);
                window.emoj = false;
            }
        } else {
            console.warn("emojioneArea plugin is not available");
        }

        setComposeNotification(prev => ({
            ...prev,
            usersGroupId: selectedNotificationThreadUuid,
            emailOnlySubscribers: emailOnlySubscribers
        }));
        setDisableEditor(false);
        
        // Cleanup function to prevent memory leaks
        return () => {
            if (window.emoj && window.emoj[0] && window.emoj[0].emojioneArea) {
                try {
                    window.emoj[0].emojioneArea.off("emojibtn.click");
                } catch (error) {
                    console.warn("Failed to cleanup emojioneArea:", error);
                }
            }
        };

    }, [selectedNotificationThreadUuid, emailOnlySubscribers]);

    // Separate effect for handling private recipient details
    useEffect(() => {
       // if selected channel contains "5" then there must be one user selected, fetch the details using getPrivateRecipientDetail using the uuid  of the user at 0 index
       if (channels.includes("5") && createRecipient.selectedUsers.length>0) {
            const currentFirstUser = Array.isArray(createRecipient.selectedUsers) 
                ? createRecipient.selectedUsers[0] 
                : createRecipient.selectedUsers.split(',')[0];

            // Only fetch if the first user has actually changed
            if (currentFirstUser !== previousFirstUserRef.current) {
                previousFirstUserRef.current = currentFirstUser;

                getPrivateRecipientDetail(currentFirstUser).then((data)=>{
                    if(data){
                        if(data.length>0){
                            setPrivateRecipientDetails(data[0]);
                        }
                        console.log("PrivateRecipientDetails",data);
                    }else{
                        setPrivateRecipientDetails(null);
                    }
                });
            }
        }else if (!channels.includes("5")) {
            // Only clear if it's NOT a private message channel
            previousFirstUserRef.current = null;
            setPrivateRecipientDetails(null);
        }
    }, [channels, createRecipient.selectedUsers, getPrivateRecipientDetail]);

    // Effect to sync privateRecipientDetails from selectUsersForGroup for existing threads
    useEffect(() => {
        // Only update if:
        // 1. We're viewing a private message (channel 5)
        // 2. We have recipient data
        // 3. We have a selected thread (not switching/clearing)
        if (channels.includes("5") && 
            selectUsersForGroup?.previewedRecipientData && 
            selectUsersForGroup.previewedRecipientData.length > 0 &&
            selectedNotificationThreadUuid) {
            
            // Set the first recipient as private recipient details
            const firstRecipient = selectUsersForGroup.previewedRecipientData[0];
            setPrivateRecipientDetails(firstRecipient);
        } else if (!selectedNotificationThreadUuid || !channels.includes("5")) {
            // Clear privateRecipientDetails if switching away from private message or no thread selected
            setPrivateRecipientDetails(null);
        }
    }, [channels, selectUsersForGroup, selectedNotificationThreadUuid]);

    // Effect to control disableEditor based on recipient selection
    useEffect(() => {
        // Disable editor if no notification thread is selected and no recipients are selected
        if (!selectedNotificationThreadUuid && !hasRecipientsSelected) {
            setDisableEditor(true);
        } else {
            // Don't automatically enable - let other logic control this
            // (like after message send, during edit, etc.)
        }
    }, [selectedNotificationThreadUuid, hasRecipientsSelected]);

    // Effect to set inPreviewNotificationUuid when a DRAFT message is in the thread
    useEffect(() => {
        if (selectedNtification && selectedNtification.length > 0) {
            // Check if any message has DRAFT status
            const draftMessage = selectedNtification.find((msg: any) => msg.status === 'DRAFT');
            if (draftMessage) {
                setInPreviewNotificationUuid(draftMessage.id || draftMessage.messageUuid);
            } else {
                // Clear if no draft messages
                if (inPreviewNotificationUuid) {
                    setInPreviewNotificationUuid(null);
                }
            }
        } else {
            // Clear when no messages
            if (inPreviewNotificationUuid) {
                setInPreviewNotificationUuid(null);
            }
        }
    }, [selectedNtification]);

    const notificationSubjectHandler = (e) => {
        setNotificationSubject(e.target.value);
        setCurrentSubjectLenght(e.target.value.length);
    }
    const notificationMessageHandler = (e) => {
        setNotificationMessage(e.target.value)
    }

    const setCurrentContentLengthTypeHandler = (val) => {
        console.log("setCurrentContentLengthTypeHandler", val);
        setCurrentContentLengthType(val);
    }

    const getCurrentContentLength = () => {
        return currentContentLengthType;
    }



    const filesHandlerCallback = (files) => {
        //console.log('Uploaded Files ',files);
        if (!richtextEditor.current) return;
        
        if (window.UPLOAD_FILES === "IMAGES_GALLERY") {
            richtextEditor.current.insertImages(files);
        }
        if (window.UPLOAD_FILES === "VIDEO") {
            richtextEditor.current.insertVideosCustomComponent(files);
        }
        if (window.UPLOAD_FILES === "PDF_ATTACHMENTS") {
            richtextEditor.current.insertPDFAttachment(files);
        }
        if (window.UPLOAD_FILES === "IMAGE_ATTACHMENTS") {
            richtextEditor.current.insertImageAttachmentCustomComponent(files);
        }
        if (window.UPLOAD_FILES === "PDF_TO_IMAGES_ATTACHMENTS") {
            richtextEditor.current.insertPdfToImageAttachmentCustomComponent(files);
        }
        if (window.UPLOAD_FILES === "AUDIO_ATTACHMENTS") {
            richtextEditor.current.insertAudioAttachmentCustomComponent(files);
        }
    }

    const updatenotificationMessage = (message) => {
        // console.log("Message set = " + message);
        setNotificationMessage(message);
    }
    const sendNewMessage= async(messageUuid)=>{
        const res = await sendMessage(messageUuid, authCtx.token);
        
        
        // Reload threads to get updated last_notification_status from backend
        if (loadNotificationThreads) {
            try {
                await loadNotificationThreads(0, '');
            } catch (error) {
                console.error('Error reloading threads after message send:', error);
            }
        }
        
        setInPreviewNotificationUuid(null);
        return res;
    }


    const isBlankMessage = (rawMessage) => {
        let messageBlocks= JSON.parse(rawMessage).blocks??[];
        let isBlank = true;

        for (let i = 0; i < messageBlocks.length; i++) {
            if (messageBlocks[i].type !== "atomic" && messageBlocks[i].text.trim().length > 0) {
                isBlank = false;
                break;
            }
            if (messageBlocks[i].type==="atomic") {
                isBlank = false;
                break;
            }
        }
        return isBlank;
    }

    // Extract content types from message
    const extractContentTypes = (rawMessage: string) => {
        try {
            const messageObject = JSON.parse(rawMessage);
            const contentTypes: string[] = [];
            const contentTypeIds: string[] = [];

            if (messageObject.entityMap) {
                Object.values(messageObject.entityMap).forEach((entity: any) => {
                    if (SUPPORTED_CONTENT_TYPES.includes(entity.type)) {
                        contentTypes.push(entity.type);
                        const typeId = CONTENT_TYPE_MAP[entity.type];
                        if (typeId) {
                            contentTypeIds.push(typeId);
                        }
                    }
                });
            }

            return {
                contentTypeStr: contentTypes.join(','),
                contentTypeIdStr: contentTypeIds.join(',')
            };
        } catch (error) {
            console.error('Error parsing raw message:', error);
            return { contentTypeStr: '', contentTypeIdStr: '' };
        }
    };

    // Validate form inputs
    const validateForm = () => {
        if (isBlankMessage(rawMessage)) {
            toast.error("Message is blank.");
            return false;
        }

        if (currentSubjectLenght > 55 || currentContentLengthType > 240) {
            toast.error("Length of subject or message body exceeded.");
            return false;
        }

        if (isCreatingRecipient) {
            if (channels.length === 0) {
                toast.error('Please select Channels');
                return false;
            }
            if (!notificationSubject?.trim()) {
                toast.error('Subject is required.');
                return false;
            }
        } else if (composeNotification.usersGroupId === null) {
            toast.error('Please select your recipients prior to sending.');
            return false;
        }

        return true;
    };

    // Get private recipient prefix if applicable
    const getPrivateRecipientPrefix = async (groupId: string, subject: string) => {
        // Check if subject is defined and is a string
        if (!subject || typeof subject !== 'string') {
            console.warn('getPrivateRecipientPrefix called with invalid subject:', subject);
            return '';
        }
        
        if (channels.includes('5') && !subject.includes('@@@')) {
            try {
                const userDetails = await getUsersDetailBGgroup(groupId, authCtx.token);
                
                // Add proper null checks for the response structure
                if (userDetails && userDetails.data && userDetails.data.groupUser && 
                    userDetails.data.groupUser.length > 0 && userDetails.data.groupUser[0].username) {
                    return userDetails.data.groupUser[0].username + "@@@";
                } else {
                    console.warn('getUsersDetailBGgroup returned unexpected structure:', userDetails);
                    return '';
                }
            } catch (error) {
                console.error('Error fetching private recipient details:', error);
                return '';
            }
        }
        return '';
    };

    // Get draft suffix
    const getDraftSuffix = (subject: string) => {
        return subject.includes('-#DRFT#') ? '' : '-#DRFT#';
    };

    // Reset form state
    const resetFormState = () => {
        setIsSending(false);
        //setNotificationSubject('');
        setNotificationMessage('');
        setRawMessage('');
        if (richtextEditor.current) {
            richtextEditor.current.reInitializeForEdit('', '');
        }
    };

    /* 
    
    this function will accept the response from create notification api and it add the notification to the list in the parent component
    */
    const handleNotificationSuccess = (res: any, emailOnlySubscribers?: any[]) => {
        // Validate response structure
        // Response can be either an array directly or wrapped in res.data
        let notificationData;
        
        if (Array.isArray(res)) {
            // Response is an array directly
            notificationData = res[0];
        } else if (res && res.data && Array.isArray(res.data)) {
            // Response is wrapped in res.data
            notificationData = res.data[0];
        } else {
            console.error('Invalid response structure:', res);
            toast.error('Notification created but response format was unexpected');
            //setDisableEditor(true);
            setInPreviewNotificationUuid(null);
            resetFormState();
            return;
        }

        if (!notificationData) {
            console.error('No notification data found in response:', res);
            toast.error('Notification created but no data was returned');
            //setDisableEditor(true);
            setInPreviewNotificationUuid(null);
            resetFormState();
            return;
        }

        console.log('Processing notification data:', notificationData);
        console.log('Current usersGroupId:', composeNotification.usersGroupId);
        console.log('Is creating recipient:', isCreatingRecipient);

        //setDisableEditor(true);
        
        // Set the in-preview notification UUID
        setInPreviewNotificationUuid(notificationData.id || notificationData.messageUuid);
        
        const messagesCreated = {
            ...notificationData,
            messageUuid: notificationData.id, // Ensure messageUuid field is set
            //usersGroupId: composeNotification.usersGroupId, // Explicitly include the group ID
            channels: Array.isArray(channels) ? channels.join(",") : channels,
            notificationThreadStatus: "ACTIVE",
            ...(emailOnlySubscribers && { emailOnlySubscribers })
        };
        
        console.log('Adding message to list:', messagesCreated);
        addNotificationsToList(messagesCreated, isCreatingRecipient);
        resetFormState();
        
        if (isCreatingRecipient) {
            setIsCreatingRecipient(false);
            setHasRecipientsSelected(false);
            setCreateRecipient({
                selectedUsers: [],
                selectedTags: [],
                selectedChanels: '',
                name: '',
                createdBy: '',
                emailOnlySubscribers: []
            });
        }
    };

    // Handle errors
    const handleError = (error: any) => {
        console.error('Error:', error);
        toast.error(error.message || error);
        setIsSending(false);
    };

    // Create new recipient and notification 
    const handleCreateNewRecipient = async (contentTypeStr: string, contentTypeIdStr: string) => {
        const updateGroupRecipient: CreateContactGroupRequest = {
            ...createRecipient,
            selectedChanels: channels.join(','),
            name: notificationSubject,
            previewedRecipientData: getFormattedRecipientData.previewedRecipientData,
            selectedUsers: Array.isArray(createRecipient.selectedUsers) 
                ? createRecipient.selectedUsers.join(',') 
                : createRecipient.selectedUsers,
            selectedTags: Array.isArray(createRecipient.selectedTags) 
                ? (createRecipient.selectedTags as (string | number)[]).join(',') 
                : createRecipient.selectedTags
        };

        try {
            const resp = await createContactGoup(updateGroupRecipient, authCtx.token);
            console.log('Create contact group response:', resp);
            
            const groupId = (resp as any).groupUUID || (resp as any).groupID || (resp as any).id || (resp as any).uuid;
            if (!groupId) {
                throw new Error('Group ID not found in response');
            }

            // set the selectUsersForGroup value in parent component for the created group , fetch this new created group detail
            const updateGroupRecipientDetail = await getSelectUserGroupForDM(groupId, authCtx.token);
            //updateGroupRecipientDetail.previewedRecipientData = JSON.parse(updateGroupRecipientDetail.previewedRecipientData);
            //updateGroupRecipientDetail.total = updateGroupRecipientDetail.previewedRecipientData.length
            // Update the selected notification thread UUID with the newly created group
            propsSetSelectedNotificationThreadUuid(groupId);
            // Sync the title shown in the header for this newly created thread
            setSelectedGroupTitle(notificationSubject);
            // set private recipient details if applicable
            if (channels.includes("5") && updateGroupRecipientDetail.previewedRecipientData.length > 0) {
                setPrivateRecipientDetails(updateGroupRecipientDetail.previewedRecipientData[0]);
            }
            guardedSetSelectUsersForGroup(updateGroupRecipientDetail);
            
            console.log('Setting selectUsersForGroup with fetched data:', updateGroupRecipientDetail);

            // Now create the notification for this new group or Notification Thread.
            
            // For private messages (channel 5), use the recipient's name directly from privateRecipientDetails
            let pvtRecipient = '';
            if (channels.includes("5") && privateRecipientDetails) {
                // Use the recipient's first name and last name to create the prefix
                const recipientName = `${privateRecipientDetails.firstName || ''} ${privateRecipientDetails.lastName || ''}`.trim();
                pvtRecipient = recipientName ? recipientName + "@@@" : '';
            } else if (channels.includes("5")) {
                // Fallback to fetching from group if privateRecipientDetails not available
                pvtRecipient = await getPrivateRecipientPrefix(groupId, notificationSubject);
            }
            
            const draft = getDraftSuffix(notificationSubject);
            const emailOnlySubscribers = (resp as any)?.groupDetail?.emailOnlySubscribers;

            const updateComposeNotification = {
                ...composeNotification,
                title: pvtRecipient + notificationSubject + draft,
                messageBody: notificationMessage,
                messageCategory: contentTypeStr,
                categoryId: contentTypeIdStr,
                usersGroupId: groupId,
                fkGroupId: groupId, // Add this field for server compatibility
                schoolName: authCtx.userData.orgName,
                rawMessageBody: rawMessage,
                selectedRecipient: createRecipient,
                emailOnlySubscribers: emailOnlySubscribers,
                // Pass recipient data directly since prop won't update until next render
                recipientData: updateGroupRecipientDetail
            };

            console.log('Notification data being sent:', updateComposeNotification);
            // create new notification for the newly created group
            const res = await createNewNotification(updateComposeNotification, authCtx.token);
            // Add usersGroupId and recipientData to the response object
            (res[0] as any).usersGroupId = groupId;
            (res[0] as any).recipientData = updateGroupRecipientDetail;
            handleNotificationSuccess(res, emailOnlySubscribers);
        } catch (error) {
            handleError(error);
        }
    };

    // Edit existing message
    const handleEditMessage = async () => {
        updateNotificationAtUuid(messageToEdit.id, notificationMessage, rawMessage);

        const updateComposeNotification: CreateNotificationRequest = {
            ...messageToEdit,
            title: groupFirstNotificationSubject[0]?.title,
            messageBody: notificationMessage,
            selectedRecipient: createRecipient,
            rawMessageBody: rawMessage,
        };

        try {
            await editNotificationService(messageToEdit.id, updateComposeNotification, authCtx.token);
            toast.success("Message updated successfully!", {
                position: toast.POSITION.BOTTOM_RIGHT,
                type: 'success'
            });
            setMessageToEdit(false);
            resetFormState();
        } catch (error) {
            handleError(error);
        }
    };

    // Create notification for existing group
    const handleCreateForExistingGroup = async (contentTypeStr: string, contentTypeIdStr: string) => {
        const pvtRecipient = await getPrivateRecipientPrefix(composeNotification.usersGroupId!, selectedGroupTitle);
        const draft = getDraftSuffix(selectedGroupTitle);
        const finalTitle = pvtRecipient + selectedGroupTitle + draft;
        const updateComposeNotification: CreateNotificationRequest = {
            ...composeNotification,
            title: finalTitle,
            messageBody: notificationMessage,
            messageCategory: contentTypeStr,
            categoryId: contentTypeIdStr,
            schoolName: authCtx.userData.orgName,
            rawMessageBody: rawMessage,
        };

        try {
            const res = await createNewNotification(updateComposeNotification, authCtx.token);
            (res[0] as any).usersGroupId = selectedNotificationThreadUuid;
            
        
            handleNotificationSuccess(res);
        } catch (error) {
            handleError(error);
        }
    };

    const submitHandler = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        const { contentTypeStr, contentTypeIdStr } = extractContentTypes(rawMessage);
        setIsSending(true);

        try {
            if (isCreatingRecipient) {
                await handleCreateNewRecipient(contentTypeStr, contentTypeIdStr);
            } else if (messageToEdit !== false) {
                await handleEditMessage();
            } else {
                await handleCreateForExistingGroup(contentTypeStr, contentTypeIdStr);
            }
        } catch (error) {
            handleError(error);
        }
    };
    // open/cloase Recipient modal
    const recipientModalHendler = () => {
        //alert(1);
        //console.log(selectedNotificationThreadUuid+'====='+currentSelectedGroup.uuid);
        if (!isRecipientModalOpen && selectedNotificationThreadUuid) {

            getSelectedRecipientData(selectedNotificationThreadUuid, authCtx.token)
                .then((response) => {
                    console.log('getRecipientData',response.data);
                    const sliceCatTags = [];
                    const categoriesTags = response.data.userGroupCategories || [];

                    if (categoriesTags.length > 0) {
                        // For now, creating empty structure until we understand the API response better
                        categoriesTags.forEach(catTags => {
                            const temp = {
                                [catTags.categoryID]: [] // Empty array for tags - will need to be populated with actual API response
                            }
                            sliceCatTags.push(temp);
                        });
                    }
                    const selectedusersStr = response.data.selectedUsers;
                    //console.log('selectedUserStr',selectedusersStr);
                    const updateSelectedUsers = !selectedusersStr ? [] : selectedusersStr.split(",");
                    
                    // Update createRecipient with all the data
                    setCreateRecipient(prev => ({
                        ...prev,
                        selectedUsers: updateSelectedUsers,
                        selectedTags: sliceCatTags,
                        emailOnlySubscribers: response.data.emailOnlySubscribers
                    }));
                    
                    if(response.data.isAllSchoolLinkAppUsersSelected==="true"){
                        setIsAllSchoolLinkAppUsersSelected(true);
                    }
                    if(response.data.isAllEmailOnlySubscribersSelected==="true"){
                        setIsAllEmailOnlySubscribersSelected(true);
                    }
                    //setIsAllEmailOnlySubscribersSelected(response.data.isAllEmailOnlySubscribersSelected);

                }).catch((error) => {
                    console.log(error);
                })
        }else{
            //setSelectedUsers([]);
        }
        setIsRecipientModalOpen((prevState) => !prevState);
    }
    // set the contact group uuid on the create notification object
    const saveNotificationThreadConfig = (contactGroupId) => {
        console.log('saveNotificationThreadConfig called with:', contactGroupId);
        console.log('previewedRecipientData:', contactGroupId.previewedRecipientData);
        console.log('recipientCount from modal:', contactGroupId.recipientCount);
        console.log('Current selectUsersForGroup:', selectUsersForGroup);
        
        setCreateRecipient(contactGroupId);
        setIsCreatingRecipient(true);
        
        // CRITICAL: Update selectUsersForGroup to enable the editor
        // The overlay blocking the editor checks getFormattedRecipientData which derives from selectUsersForGroup
        let recipientData = contactGroupId.previewedRecipientData;
        let recipientCount = contactGroupId.recipientCount || 0;
        
        // Check if we have actual preview data
        if (recipientData && Array.isArray(recipientData) && recipientData.length > 0) {
            recipientCount = recipientCount || recipientData.length;
            console.log('Using provided previewedRecipientData:', recipientData);
        } 
        // Fallback 1: Use selectedUsers if available
        else if (contactGroupId.selectedUsers && contactGroupId.selectedUsers.length > 0) {
            recipientData = contactGroupId.selectedUsers.map((userId, index) => ({
                uuid: userId,
                id: index + 1,
                name: `User ${index + 1}`,
                email: '',
                _placeholder: true
            }));
            recipientCount = recipientCount || recipientData.length;
            console.log('Created placeholder recipients from users:', recipientData);
        }
        // Fallback 2: Check if tags are selected (means recipients exist but not individually listed yet)
        else if (contactGroupId.selectedTags && 
                 ((Array.isArray(contactGroupId.selectedTags) && contactGroupId.selectedTags.length > 0) ||
                  (typeof contactGroupId.selectedTags === 'string' && contactGroupId.selectedTags.trim().length > 0))) {
            // Use recipientCount from modal if provided, otherwise create placeholder
            recipientCount = recipientCount || 1;
            recipientData = [{
                uuid: 'tag-based',
                id: 1,
                name: 'Recipients based on selected tags/categories',
                email: '',
                _placeholder: true,
                _isTagBased: true
            }];
            console.log('Created placeholder for tag-based recipients, count:', recipientCount);
        }
        // No recipients
        else {
            recipientData = [];
            recipientCount = 0;
            console.log('No recipients selected');
        }
        
        console.log('Final - Setting selectUsersForGroup with:', {
            previewedRecipientData: recipientData,
            total: recipientCount
        });

       
        guardedSetSelectUsersForGroup({
            previewedRecipientData: recipientData,
            total: recipientCount
            
        });
     
           setSelectedRecipientsData({
            previewedRecipientData: recipientData,
            total: recipientCount
            
        });

        setPreviewedRecipientReceivedBasedOnConfigurationOfNotificationThread(recipientData || []);
        // Enable editor if we have any recipients
        const hasRecipients = recipientCount > 0 || (recipientData && recipientData.length > 0);
        setHasRecipientsSelected(hasRecipients);
        setDisableEditor(!hasRecipients);
        
        resetNotificationBox();
    }


    const showEmojiPicker = () => {
        if (window.emoj && window.emoj[0] && window.emoj[0].emojioneArea) {
            try {
                window.emoj[0].emojioneArea.showPicker();
            } catch (error) {
                console.warn("Failed to show emoji picker:", error);
            }
        } else {
            console.warn("emojioneArea is not initialized");
        }
    }
    const getMessageContent = () => {
        return notificationMessage;
    }
    // on create pin message
    const savePinMessageHandler = (messageId) => {
        onCreatePinMessage(messageId);
    }



    //Active Editor
    let currentEditor = null;
    if (channels.includes("3")) {
        currentEditor = <textarea onChange={notificationMessageHandler} value={notificationMessage}
            className={'form-control pl-2 my-0 ' + styles.inputbox}
            placeholder="Type your message here..." />;
    } else if (activeEditor === 'RICH_TEXT') {
        currentEditor = <RichTextMessageEditor
            notificationMessage={notificationMessage}
            rawMessage={rawMessage}
            updatenotificationMessage={updatenotificationMessage}
            setRawMessage={setRawMessage}
            getRawMessage={getRawMessage}
            setCurrentContentLengthType={setCurrentContentLengthTypeHandler}
            ref={richtextEditor}
            setSubjectInputFocused={setSubjectInputFocused}
            setMessageInputFocused={setMessageInputFocused}
            refMoreTextBlockEditor={refMoreTextBlockEditor}
            setmoreTextBlockEditorState={setmoreTextBlockEditorState}
            getMessageContent={getMessageContent} />;
    } else {
        currentEditor = <label>Please select Editor</label>;
    }


    const editMessage = (messageObject) => {
        setMessageToEdit(messageObject);
        updateNotificationFlagAtUuid(messageObject.id,'EDITING');
        //debugger;
        if (richtextEditor.current) {
            richtextEditor.current.reInitializeForEdit(messageObject.messageBody, messageObject.rawMessageBody);
        }
        setNotificationMessage(messageObject.messageBody);
        setRawMessage(messageObject.rawMessageBody);
        setDisableEditor(false);
        setInPreviewNotificationUuid(null);
    }

    const cancelEditing = useCallback((e)=>{
        if (e) {
            e.preventDefault();
        }
        if (richtextEditor.current) {
            richtextEditor.current.reInitializeForEdit('', '');
            setNotificationMessage('');
            setRawMessage('');
            if(selectedNtification.length===0){
                setNotificationSubject('');
            }
        }
        if (messageToEdit !== false) {
            updateNotificationFlagAtUuid(messageToEdit.id,'');
        }
        setMessageToEdit(false);
        // Keep editor enabled so user can write a new message
        // setDisableEditor(true);
        setInPreviewNotificationUuid(null);
    }, [selectedNtification]);

    useImperativeHandle(ref, () => ({

        clearEditor() {
            cancelEditing(undefined);
        },
        clearRecipientsSelection() {
            setCreateRecipient({
                selectedUsers: [],
                selectedTags: [],
                selectedChanels: '',
                name: '',
                createdBy: '',
                emailOnlySubscribers: []
            });
            setIsAllSchoolLinkAppUsersSelected(false);
            setIsAllEmailOnlySubscribersSelected(false);
            setIsCreatingRecipient(false);
            setHasRecipientsSelected(false);
            richtextEditor.current.reInitializeForEdit('', '');
            setNotificationSubject('');
            propsSetSelectUsersForGroup({previewedRecipientData:[],total:0});
        },
        setPreviewedRecipientData(data) {
            // Update through props callback since we removed local state
            // parse data if it's in string format
            if (typeof data === 'string') {
                data = JSON.parse(data);
            }
            propsSetSelectUsersForGroup({
                previewedRecipientData: data,
                total: data.length
            });
        }

    }),[cancelEditing, propsSetSelectUsersForGroup]);

    const removeNotification=(id, shouldRemoveGroup)=>{
        onRemoveNotification(id,shouldRemoveGroup);
    }
    console.log("isCreatingRecipient",isCreatingRecipient)

    // Helper function to safely parse JSON
    // (Moved to NotificationThreadHeader component)

      const setSelectedUsersWrapper=(users)=>{
        // if channel has the value 5 then only one user can be selected for private message
        let finalUsers = users;
        if(channels.includes("5")){
            if(users.length>1){
                finalUsers=users.slice(-1);
            }
        }
        setCreateRecipient(prev => ({
            ...prev,
            selectedUsers: finalUsers
        }));
      }

    // Memoized callbacks to prevent infinite re-renders in NotificationRecipientPreview
    // These callbacks include a guard to only update if we're still on the same thread
    const handleSetRecipientsCount = useCallback((count) => {
        // Only update if we still have a selected thread (haven't switched/cleared)
        if (selectedNotificationThreadUuid) {
            guardedSetSelectUsersForGroup(prev => ({...prev, total: count}));
        }
    }, [guardedSetSelectUsersForGroup, selectedNotificationThreadUuid]);

    const handleSetPreviewedRecipientData = useCallback((data) => {
        // Only update if we still have a selected thread (haven't switched/cleared)
        if (selectedNotificationThreadUuid) {
            guardedSetSelectUsersForGroup({previewedRecipientData: data, total: data?.length || 0});
        }
    }, [guardedSetSelectUsersForGroup, selectedNotificationThreadUuid]);

    const handleSetPreviewedRecipientDataTemp = useCallback((data) => {
        // This is for temporary preview data - currently just logs
        console.log('setPreviewedRecipientDataTemp called with data length:', data?.length);
    }, []);


    return (
        <div className={common.rightContainer}>


            {isRecipientModalOpen && <NotificationRecipientsSelectorDialog
                modalStatus={isRecipientModalOpen}
                onAction={recipientModalHendler}
                saveNotificationThreadConfig={saveNotificationThreadConfig}
                channels={channels}
                selectedUsers={Array.isArray(createRecipient.selectedUsers) ? createRecipient.selectedUsers : []}
                setSelectedUsers={setSelectedUsersWrapper}
                setSelectUsersForGroup={guardedSetSelectUsersForGroup}
                selectedCategoriesTags={Array.isArray(createRecipient.selectedTags) ? createRecipient.selectedTags as any : []}
                selectedEmailOnlySubscribers={(createRecipient.emailOnlySubscribers as any) || []}
                isAllSchoolLinkAppUsersSelected={isAllSchoolLinkAppUsersSelected}
                isAllEmailOnlySubscribersSelected={isAllEmailOnlySubscribersSelected}
                setRecipientsCount={handleSetRecipientsCount}
                setSelectedCategoryTags={(tags) => setCreateRecipient(prev => ({...prev, selectedTags: tags as any}))}
                setselectedEmailOnlySubscribers={(subs) => setCreateRecipient(prev => ({...prev, emailOnlySubscribers: subs as any}))}
                setIsAllSchoolLinkAppUsersSelected={setIsAllSchoolLinkAppUsersSelected}
                setIsAllEmailOnlySubscribersSelected={setIsAllEmailOnlySubscribersSelected}
                isRecipientUpdate={selectedNotificationThreadUuid ? selectedNotificationThreadUuid : false}
                setPreviewedRecipientData={handleSetPreviewedRecipientData}
                previewedRecipientData={getFormattedRecipientData.previewedRecipientData || []}
                onNavigateToDmThread={props.onNavigateToDmThread ? (groupUuid) => {
                    props.onNavigateToDmThread!(groupUuid, notificationThreadStatus, channels, selectedGroupTitle);
                } : undefined}
            />}


            <NotificationThreadHeader
                selectedNotificationThreadUuid={selectedNotificationThreadUuid}
                selectedGroupTitle={selectedGroupTitle}
                channels={channels}
                recipientData={getFormattedRecipientData}
                recipientModalHendler={recipientModalHendler}
                isCreatingRecipient={isCreatingRecipient}
                privateRecipientDetails={privateRecipientDetails}
            />
            <div className={common.editorInner+' '+common.secondBlock}>


                {isCardOpen && <NotificationCardList
                    selectedNotificationData={selectedNtification}
                    removeNotification={removeNotification}
                    onSavePinMessage={savePinMessageHandler}
                    onEditMessage={editMessage}
                    sendMessage={sendNewMessage}
                    setDissableEditor={setDisableEditor}
                    notificationThreadStatus={notificationThreadStatus}
                    onResendSuccess={(messageData: any) => {
                        if (!messageData) {
                            return;
                        }
                        const normalizedMessage = {
                            ...messageData,
                            messageUuid: messageData.messageUuid || messageData.id,
                            channels: Array.isArray(channels) ? channels.join(',') : channels,
                            notificationThreadStatus: 'ACTIVE',
                            last_notification_status: 'ACTIVE',
                            emailOnlySubscribers: emailOnlySubscribers || []
                        };
                        addNotificationsToList(normalizedMessage, false);
                    }}
                />}
                {!isCardOpen && <div style={{ height: "auto" }}></div>}
            </div>

                {/* Border to separate list and editor */}

            <form className={"form-group basic-textarea " + notificationThreadStatus + ' ' + EditorStyle.editorView + ' ' + common.editorbody+' '+common.thirdBlock}
                style={{ display: (notificationThreadStatus === "INACTIVE") ? "none" : "" }} onSubmit={submitHandler}>
                <div style={{ borderBottom: "1px solid rgb(244 248 251)", width: "536px", marginLeft: "15px", boxShadow: "0 -1px 1px rgba(108, 107, 107, 0.23)", zIndex: "99" }}></div>

                {/* Show overlay when editor is disabled */}
                {disableEditor && (
                    <div style={{backgroundColor:'rgb(251 251 251 / 50%)', height:'100%', width:'100%', position:'absolute', zIndex:'2', backdropFilter:'blur(2px)'}}>
                       
                            <span 
                                style={{ position: "relative", top: "80px", left: "180px", color: "#f00a", border:"1px solid #cccccc", borderRadius:"5px", padding:"7px", background:"#ffffff", boxShadow:"1px 1px 3px #ccc", cursor:"pointer"}} 
                                onClick={recipientModalHendler}
                            >
                                Please select your recipients.
                            </span>
                    
                    </div>
                )}

                {inPreviewNotificationUuid != null && messageToEdit === false && (
                    <div style={{backgroundColor:'rgb(251 251 251 / 50%)', height:'100%', width:'100%', position:'absolute', zIndex:'2', backdropFilter:'blur(2px)'}}>
                       
                             <span 
                                style={{ position: "relative", top: "80px", left: "180px", color: "#f00a", border:"1px solid #cccccc", borderRadius:"5px", padding:"7px", background:"#ffffff", boxShadow:"1px 1px 3px #ccc", cursor:"pointer"}} 
                                onClick={recipientModalHendler}
                            >
                                Unsent message in DRAFT form.

                            </span>
                    
                    </div>
                )}

                <div className="container">
                    <div className="row">
                        <div className="col-3">
                            <div id="selectedChannelsInComposer" className='aaa'
                                style={{
                                    marginTop: "0",
                                    padding: '10px 5px',
                                    top: '10px',
                                    float: "left"
                                }}>
                                {CHANNELS.map((channel) => {
                                    return channels.includes(channel.id) &&
                                    <OverlayTrigger
                                    key={channel.id}
                                    placement="top"
                                    delay={{ show: 50, hide: 400 }}
                                    overlay={(props) => (
                                        <Tooltip {...props}>
                                          {channel.name}
                                        </Tooltip>
                                      )}
                                    >
                                  <span><SVGIcon svgIcondata={channel.svgIcon} /> </span>
                                </OverlayTrigger>



                                })
                                }
                            </div>
                        </div>
                        <div className="col-9" style={{"paddingRight":"25px"}}>
                            {isSubjectInputFocused &&
                                <div className={styles.ContentLengthDispaly}>
                                    {currentSubjectLenght > 55 && <span className={styles.invaliderror}>Character limit(max 65) exceeded for subject line.</span>}
                                    {currentSubjectLenght <= 55 && <span>Characters remaining for subject: {(55 - currentSubjectLenght)}/55</span>}
                                </div>
                            }
                            {isMessageInputFocused &&
                                <div className={styles.ContentLengthDispaly}>
                                    {currentContentLengthType > 240 && <span className={styles.invaliderror}>Character limit(max 240) exceeded for message body.</span>}
                                    {currentContentLengthType <= 240 && <span>Characters remaining for message body: {(240 - currentContentLengthType)}/240</span>}
                                </div>
                            }
                        </div>
                    </div>
                </div>

                {/*Editor Area starts from here*/}
                <div className={styles.composemsgbox}>
                    {!selectedNotificationThreadUuid && (
                        <><input onFocus={() => { setSubjectInputFocused(true); setMessageInputFocused(false) }}
                            onChange={notificationSubjectHandler}
                            type="text"
                            className={'form-control  pl-2 my-0 ' + styles.inputbox + ' ' + styles.subjectLine}
                            placeholder="Type your subject here..."
                            value={notificationSubject.replace("-#DRFT#","")}
                                 id="message-subject-line"
                        />
                            <div className={styles.inputline}></div>
                        </>
                    )}

                    {currentEditor}
                    <FileUpload filesHandlerCallback={filesHandlerCallback} />

                </div>
                {/*Editor Area ends from here*/}


                {/*Bottom buttons area starts*/}
                <div className={styles.features}>

                    <div className={styles.bottomBorder}>
                        <RichTextEditorToolbar
                            setActiveEditor={setActiveEditor}
                            showEmojiPicker={showEmojiPicker}
                            richtextEditor={richtextEditor}
                            channels={channels}
                            getCurrentContentLength={getCurrentContentLength}
                            moreTextBlockEditorState={moreTextBlockEditorState}
                        />
                        {(currentContentLengthType > 0) && <button onClick={cancelEditing} style={{marginRight:'10px',border:'1px solid #d1d1d6',backgroundColor:'transparent',padding:'5px 10px',color:'#d1d1d6'}} className={button.btn + ' ' + button + ' ' + styles.sendButton} > Cancel</button>  }
                        <button type="submit"
                            disabled={(channels.includes("3") ? notificationMessage.trim().length === 0 : rawMessage.trim().length === 0) || isSending}
                            className={button.btn + ' ' + button.blueButton + ' ' + styles.sendButton}
                            style={{ position: "inherit", float: 'right' }}>

                            {!isSending ? (messageToEdit !== false ? 'Save' : 'Preview') : ''}
                            {isSending && <> <span className="spinner-border spinner-border-sm" role="status"
                                aria-hidden="true"></span> {messageToEdit !== false ? 'Save...' : 'Preview...'}</>
                            }
                        </button>
                    </div>
                </div>
                {/*Bottom buttons area ends*/}
            </form>

            <div className={common.fourthBlock} style={{ fontSize: "12px", textAlign: "center", margin: "6px 0", color: "#888888" }}>All Rights Reserved {new Date().getFullYear()}
            </div>
        </div>
    );
})

export default NotificationThread;
